import environment from "../environment";
import type { Component, JSX } from "solid-js";
import { For, Match, Switch } from "solid-js";
import posthog from "posthog-js";
import { useAuthDispatch, useAuthState } from "../state/auth";
import { isEnabled } from "@sentry/solid";
import { useTranslation } from "../state/i18n";
import { useLocalUserSettings, useLocalUserSettingsDispatch } from "../state/localUserSettings.tsx";

const DeveloperPage = () => {
  const { currentLanguage } = useTranslation();
  const { loginStatus, impersonation, user } = useAuthState();
  const { refreshAccessToken } = useAuthDispatch();
  const localUserSettings = useLocalUserSettings();
  const localUserSettingsDispatch = useLocalUserSettingsDispatch();

  return (
    <div class="flex flex-col gap-2">
      <h2 class="text-header-xl">Environment</h2>
      <dl>
        <Item key="Version" value={import.meta.env.VITE_VERSION ?? "-"} />
        <Item key="Commit" value={import.meta.env.VITE_COMMIT_SHA} />
        <Item key="Deployment" value={environment.deployment} />
        <Item key="Is local" value={environment.isLocal()} />
        <Item key="Is development" value={environment.isDevelopment()} />
        <Item key="Is production" value={environment.isProduction()} />
        <Item key="Is live" value={environment.isLive()} />
      </dl>

      <h2 class="text-header-xl">Authentication</h2>
      <dl>
        <Item key="Login status" value={loginStatus()} />
        <Item key="User" value={user()?.profile.sub} />
        <Item key="Impersonating" value={impersonation()?.value.toString() ?? "No"} />
        <Item
          key="Refresh access token"
          value={
            <button class="rounded-sm border" onClick={() => refreshAccessToken()}>
              Refresh
            </button>
          }
        />
      </dl>

      <h2 class="text-header-xl">Feature flags</h2>
      <dl>
        <For each={Object.values(posthog.featureFlags.getFlagsWithDetails())}>
          {(flag) => <Item key={flag.key} value={flag.enabled} />}
        </For>
      </dl>

      <h2 class="text-header-xl">Local user settings</h2>
      <dl>
        <Item key="Stored settings" value={JSON.stringify(localUserSettings)} />
        <Item
          key="Clear stored settings"
          value={
            <button
              class="rounded-sm border"
              onClick={() =>
                localUserSettingsDispatch((prev) =>
                  Object.keys(prev).reduce<Record<string, undefined>>((acc, key) => {
                    acc[key] = undefined;
                    return acc;
                  }, {}),
                )
              }
            >
              Clear
            </button>
          }
        />
      </dl>

      <h2 class="text-header-xl">Other</h2>
      <dl>
        <Item key="language" value={currentLanguage()} />
        <Item key="Sentry enabled" value={isEnabled()} />
        <Item key="Posthog opted out capturing" value={posthog.has_opted_out_capturing()} />
        <Item key="Posthog disable session recording" value={posthog.config.disable_session_recording} />
      </dl>
    </div>
  );
};

const Item: Component<{ key: string; value: JSX.Element }> = (props) => (
  <div class="flex gap-2">
    <dt class="text-medium-semibold">{props.key}:</dt>
    <dd>
      <Switch fallback={props.value}>
        <Match when={typeof props.value === "boolean"}>{props.value?.toString()}</Match>
      </Switch>
    </dd>
  </div>
);

export default DeveloperPage;
